import { t } from '../i18n/translations';
import { ApiService } from '../services/api';

export class LandingPage {
  private activeTab: 'create' | 'join' = 'create';
  private createdMeeting: { roomId: string; hostToken: string; joinUrl: string } | null = null;
  private isSubmitting = false;
  private errorMessage = '';

  public onNavigateToRoom?: (roomId: string, hostToken?: string) => void;

  public render(container: HTMLElement) {
    container.innerHTML = `
      <div class="landing-container">
        <div id="nav-mount"></div>

        <main class="landing-hero">
          <div class="hero-pill">
            <span style="width: 8px; height: 8px; border-radius: 50%; background: var(--color-success); display: inline-block;"></span>
            ${t('noAccountNeeded')}
          </div>

          <h1 class="hero-title">
            ${t('heroTitlePrefix')} <br />
            <span class="text-gradient">${t('heroTitleHighlight')}</span>
          </h1>

          <p class="hero-subtitle">
            ${t('heroSubtitle')}
          </p>

          <div class="hero-card">
            ${this.createdMeeting ? this.renderSuccessView() : this.renderFormView()}
          </div>
        </main>

        <section class="landing-features">
          <div class="feature-box">
            <div class="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h3>${t('featurePrivateTitle')}</h3>
            <p style="margin-top: 8px; font-size: 0.9rem; color: var(--text-secondary); line-height: 1.5;">
              ${t('featurePrivateDesc')}
            </p>
          </div>

          <div class="feature-box">
            <div class="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </div>
            <h3>${t('featureEngineTitle')}</h3>
            <p style="margin-top: 8px; font-size: 0.9rem; color: var(--text-secondary); line-height: 1.5;">
              ${t('featureEngineDesc')}
            </p>
          </div>

          <div class="feature-box">
            <div class="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            </div>
            <h3>${t('featureFreeTitle')}</h3>
            <p style="margin-top: 8px; font-size: 0.9rem; color: var(--text-secondary); line-height: 1.5;">
              ${t('featureFreeDesc')}
            </p>
          </div>
        </section>

        <footer class="app-footer">
          <div class="footer-inner">
            <div class="footer-brand-col">
              <div class="footer-logo">
                <span class="footer-logo-icon">⚡</span>
                <span class="footer-logo-title">Verve</span>
              </div>
              <p class="footer-tagline">High-Performance, Zero-Account Video Conferencing • Powered by Solveran.</p>
            </div>
            <div class="footer-meta-col">
              <p class="footer-copyright">
                &copy; <span id="current-year">${new Date().getFullYear()}</span> <strong>Solveran</strong>. All rights reserved.
              </p>
              <p class="footer-attribution">
                Developed by 
                <a href="https://www.linkedin.com/in/hredhayxz" target="_blank" rel="noopener noreferrer" class="developer-link" title="Md Alhaz Mondal Hredhay on LinkedIn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style="display: inline-block; vertical-align: -2px; margin-right: 4px;">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                  </svg>
                  <span>Md Alhaz Mondal Hredhay</span>
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    `;

    this.bindEvents(container);
  }

  private renderFormView(): string {
    return `
      <div class="card-tabs">
        <button id="tab-create" class="card-tab ${this.activeTab === 'create' ? 'active' : ''}">
          ${t('createMeeting')}
        </button>
        <button id="tab-join" class="card-tab ${this.activeTab === 'join' ? 'active' : ''}">
          ${t('joinMeeting')}
        </button>
      </div>

      ${this.errorMessage ? `
        <div style="background: var(--color-danger-bg); border: 1px solid var(--color-danger); color: var(--color-danger); padding: 10px 14px; border-radius: var(--radius-md); font-size: 0.875rem; margin-bottom: 1rem;">
          ${this.errorMessage}
        </div>
      ` : ''}

      ${this.activeTab === 'create' ? `
        <form id="form-create">
          <div class="form-group">
            <label class="form-label">${t('meetingTitle')}</label>
            <input type="text" id="input-title" class="form-input" placeholder="e.g. Weekly Product Sync" maxlength="60" />
          </div>

          <div class="form-group">
            <label class="form-label">${t('passcodeOptional')}</label>
            <input type="password" id="input-passcode" class="form-input" placeholder="${t('passcodePlaceholder')}" maxlength="20" />
          </div>

          <div class="form-group">
            <label class="form-label">${t('profilePicture')}</label>
            <select id="select-avatar-policy" class="form-input" style="padding: 10px 12px; font-size: 0.9rem;">
              <option value="optional">${t('avatarOptional')}</option>
              <option value="required">${t('avatarRequired')}</option>
              <option value="disabled">${t('avatarDisabled')}</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-checkbox">
              <input type="checkbox" id="check-waiting-room" style="accent-color: var(--brand-primary); width: 16px; height: 16px;" />
              <span>${t('enableWaitingRoom')}</span>
            </label>
          </div>

          <button type="submit" class="btn-primary" ${this.isSubmitting ? 'disabled' : ''}>
            ${this.isSubmitting ? t('creatingMeeting') : `
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
              ${t('instantMeeting')}
            `}
          </button>
        </form>
      ` : `
        <form id="form-join">
          <div class="form-group">
            <label class="form-label">${t('enterCode')}</label>
            <input type="text" id="input-room-id" class="form-input" placeholder="${t('enterMeetingUrlOrId')}" required />
          </div>

          <button type="submit" class="btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
            ${t('joinMeeting')}
          </button>
        </form>
      `}
    `;
  }

  private renderSuccessView(): string {
    const fullUrl = `${window.location.origin}/room/${this.createdMeeting!.roomId}`;
    return `
      <div style="text-align: center; display: flex; flex-direction: column; gap: 1.25rem;">
        <div style="width: 56px; height: 56px; border-radius: 50%; background: var(--color-success-bg); color: var(--color-success); display: flex; align-items: center; justify-content: center; margin: 0 auto;">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>

        <div>
          <h2>${t('meetingCreated')}</h2>
          <p style="color: var(--text-secondary); font-size: 0.9rem; margin-top: 4px;">
            ${t('shareLinkNoAccounts')}
          </p>
        </div>

        <div style="display: flex; gap: 8px; background: var(--bg-surface-elevated); padding: 8px 12px; border-radius: var(--radius-md); border: 1px solid var(--border-subtle); align-items: center;">
          <span style="flex: 1; font-family: var(--font-mono); font-size: 0.875rem; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            ${fullUrl}
          </span>
          <button id="btn-copy-link" class="btn-secondary" style="padding: 6px 12px; font-size: 0.85rem;">
            ${t('copyLink')}
          </button>
        </div>

        <div style="display: flex; gap: 10px; margin-top: 0.5rem;">
          <button id="btn-enter-room" class="btn-primary" style="flex: 1;">
            ${t('enterMeeting')}
          </button>
        </div>
      </div>
    `;
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#tab-create')?.addEventListener('click', () => {
      this.activeTab = 'create';
      this.errorMessage = '';
      this.render(container);
    });

    container.querySelector('#tab-join')?.addEventListener('click', () => {
      this.activeTab = 'join';
      this.errorMessage = '';
      this.render(container);
    });

    container.querySelector('#form-create')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const titleInput = container.querySelector('#input-title') as HTMLInputElement;
      const passcodeInput = container.querySelector('#input-passcode') as HTMLInputElement;
      const avatarPolicySelect = container.querySelector('#select-avatar-policy') as HTMLSelectElement;
      const waitingRoomCheck = container.querySelector('#check-waiting-room') as HTMLInputElement;

      this.isSubmitting = true;
      this.errorMessage = '';
      this.render(container);

      try {
        const resp = await ApiService.createMeeting(
          titleInput?.value,
          passcodeInput?.value,
          waitingRoomCheck?.checked,
          undefined,
          avatarPolicySelect?.value || 'optional'
        );

        this.createdMeeting = {
          roomId: resp.room_id,
          hostToken: resp.host_token,
          joinUrl: resp.join_url,
        };

        // Cache host token in localStorage
        localStorage.setItem(`verve_host_${resp.room_id}`, resp.host_token);

        this.isSubmitting = false;
        this.render(container);
      } catch (err) {
        this.isSubmitting = false;
        this.errorMessage = err instanceof Error ? err.message : 'Error creating meeting';
        this.render(container);
      }
    });

    container.querySelector('#form-join')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = container.querySelector('#input-room-id') as HTMLInputElement;
      let raw = input.value.trim();

      // Support full URLs e.g. https://.../room/vrv-xxxx-yyyy
      if (raw.includes('/room/')) {
        raw = raw.split('/room/')[1].split('?')[0].split('#')[0];
      }

      if (raw && this.onNavigateToRoom) {
        this.onNavigateToRoom(raw);
      }
    });

    container.querySelector('#btn-copy-link')?.addEventListener('click', () => {
      const fullUrl = `${window.location.origin}/room/${this.createdMeeting!.roomId}`;
      navigator.clipboard.writeText(fullUrl).then(() => {
        const btn = container.querySelector('#btn-copy-link') as HTMLButtonElement;
        if (btn) btn.innerText = t('linkCopied');
        setTimeout(() => {
          if (btn) btn.innerText = t('copyLink');
        }, 2000);
      });
    });

    container.querySelector('#btn-enter-room')?.addEventListener('click', () => {
      if (this.createdMeeting && this.onNavigateToRoom) {
        this.onNavigateToRoom(this.createdMeeting.roomId, this.createdMeeting.hostToken);
      }
    });
  }
}
